import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePaLabels } from '../context/PaLabelsContext';
import { GOTO_PAGE_PATHS, pathForProfile } from '../lib/siteRoutes';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * @typedef {'simple' | 'full'} FooterVariant
 * @typedef {'partners' | 'team'} FooterFirmColumn
 */
export default function Footer({ variant = 'simple', firmColumn = 'team', omitPrivacy = false }) {
  const { t } = useTranslation('common');
  const { navPracticeLinks } = usePaLabels();
  const navigate = useNavigate();

  const go = (path) => (e) => {
    e.preventDefault();
    navigate(path);
  };

  const Link = ({ href, children }) => (
    <a href={href} onClick={go(href)}>
      {children}
    </a>
  );

  const practiceLinks = navPracticeLinks.map(({ href, label, slug }) => ({
    href,
    label,
    slug,
  }));
  const practiceLinksForVariant =
    variant === 'full' && omitPrivacy
      ? practiceLinks.filter((l) => l.slug !== 'privacy')
      : practiceLinks;

  if (variant === 'simple') {
    return (
      <footer>
        <div className="footer-top">
          <div>
            <div className="footer-logo">
              Dilinh <span>Legal</span>
            </div>
            <p className="footer-desc">{t('footer.descShort')}</p>
            <div className="footer-badges">
              <div className="footer-badge">{t('footer.badge1')}</div>
              <div className="footer-badge">{t('footer.badge2')}</div>
            </div>
          </div>
          <div>
            <div className="footer-col-title">{t('footer.practiceAreas')}</div>
            <ul className="footer-links">
              {practiceLinks.map(({ href, label }) => (
                <li key={href}>
                  <Link href={href}>{label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="footer-col-title">{t('footer.firm')}</div>
            <ul className="footer-links">
              <li>
                <Link href={GOTO_PAGE_PATHS.team}>{t('footer.ourTeam')}</Link>
              </li>
              <li>
                <Link href={GOTO_PAGE_PATHS.toolkits}>{t('footer.documentToolkits')}</Link>
              </li>
              <li>
                <Link href={GOTO_PAGE_PATHS.contact}>{t('footer.contactUs')}</Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-copy">{t('footer.copy')}</div>
        </div>
      </footer>
    );
  }

  return (
    <footer>
      <div className="footer-top">
        <div>
          <div className="footer-logo">
            Dilinh <span>Legal</span>
          </div>
          <p className="footer-desc">{t('footer.desc')}</p>
          <div className="footer-badges">
            <div className="footer-badge">{t('footer.badge1')}</div>
            <div className="footer-badge">{t('footer.badge2')}</div>
          </div>
        </div>
        <div>
          <div className="footer-col-title">{t('footer.practiceAreas')}</div>
          <ul className="footer-links">
            {practiceLinksForVariant.map(({ href, label }) => (
              <li key={href}>
                <Link href={href}>{label}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="footer-col-title">{t('footer.firm')}</div>
          <ul className="footer-links">
            {firmColumn === 'partners' ? (
              <>
                <li>
                  <Link href={pathForProfile('diep')}>Dee (Diep) Hoang</Link>
                </li>
                <li>
                  <Link href={pathForProfile('michael')}>Michael Lee</Link>
                </li>
              </>
            ) : (
              <li>
                <Link href={GOTO_PAGE_PATHS.team}>{t('footer.ourTeam')}</Link>
              </li>
            )}
            <li>
              <Link href={GOTO_PAGE_PATHS.toolkits}>{t('footer.documentToolkits')}</Link>
            </li>
            <li>
              <Link href={GOTO_PAGE_PATHS.updates}>{t('footer.legalUpdates')}</Link>
            </li>
          </ul>
        </div>
        <div>
          <div className="footer-col-title">{t('footer.contact')}</div>
          <ul className="footer-links">
            <li>
              <Link href={GOTO_PAGE_PATHS.contact}>{t('footer.contactUs')}</Link>
            </li>
            <li>
              <span className="footer-contact-line">{t('footer.addressStreet')}</span>
            </li>
            <li>
              <span className="footer-contact-line">{t('footer.addressCity')}</span>
            </li>
            <li>
              <a href="tel:+842873000864">+84 28 7300 0864</a>
            </li>
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <div className="footer-copy">{t('footer.copy')}</div>
        <LanguageSwitcher />
      </div>
    </footer>
  );
}
